/*
 * NEXT: EVERY OWED ACTION ON ITS OWN LINE, AND THE ONE YELLOW BUTTON.
 *
 * The brief's rule for this card is the strictest on the page: Next is the only
 * place an action's button lives, every owed action is its own named line (never
 * "+3 more"), only the TOP line's button is yellow and the rest are white with the
 * same verbs, and every line ends in a small × ("Not needed, or done by hand").
 * The old record broke all four at once: three yellow buttons, a Nudge under a
 * client's "done", and a reply button on the thread card as well as the bar.
 *
 * WHAT IS DECIDED HERE, AND WHAT IS NOT. Whether an action is due, who it is for
 * and what its button says are the SERVER's answers (src/actions/model.js:
 * actions[].kind, contact_id, contact_name and record_verb), because a second
 * vocabulary in the client is exactly what model.js forbids. This module only
 * arranges them: which line is on top, what the line's title and sub-line say,
 * which controls it carries, which one is yellow, and what the line becomes once
 * the email has gone (a receipt, except for "owed", which an email never clears).
 *
 *   nextModel({ act, company, automations, openAction, receipts, zones, now })
 *     → { lines, receipts, states, notes, yellow, count, countLabel, meeting }
 *
 * Pure, so test/record-next-model.test.js pins it without a browser.
 */
import { bothZones, personZones } from "./zones.mjs";
import { shortDate, relativeDay, daysBetween, plural, firstName, fullName, toDate } from "./format.mjs";
import { lastTransition } from "./situation.mjs";

/*
 * The kinds that are not Next lines. A meeting today or tomorrow is a status line
 * with Prep inside the lead block (the brief: "meeting today / tomorrow → a status
 * line with Prep, in the lead block"), and it is handed back as `meeting`.
 */
const NOT_A_LINE = new Set(["meeting_today", "meeting_soon"]);

// Kinds whose line is a hint and carries no button of its own (record_verb null).
const HINT_KINDS = new Set(["stage_update", "invite_missing"]);

/*
 * The fallback button words, used only when a payload predates record_verb (PR
 * #154). The server's ACTIONS table is the source; this is the same table, so a
 * record opened against an older server still reads the brief's verbs rather
 * than the chips' ("Respond", "Close out").
 */
export const RECORD_VERBS = {
  reply_waiting: "Reply",
  agreement_reply: "Reply",
  first_touch: "Write the first email",
  kickoff: "Send welcome email",
  finance_intro: "Send finance intro",
  slack_channel: "Create Slack channel",
  call_followup: "Follow up",
  nudge_due: "Nudge",
  final_check_due: "Check in",
  pricing_stale: "Chase",
  agreement_stale: "Chase",
  owed: "Send",
  call_actions: null,
  closing_soon: "Remove from pipeline",
  wake_up_due: "Re-engage",
  re_engage: "Re-engage",
  stage_update: null,
  invite_missing: null,
  meeting_today: null,
  meeting_soon: null,
};

/*
 * WHICH COMPOSER KIND EACH BUTTON OPENS (src/email/index.js KINDS). Re-engage is
 * built for real on Gabriel's ruling (2026-10-01, reality.txt [17]): a check-in
 * draft with the booking link inside, so the one yellow button on a wake-up day
 * does something. "owed" has no composer kind of its own yet (KINDS.owed is PR 9);
 * the server falls back to a nudge for a kind it does not know, so the record asks
 * for a follow-up, which is the nearest honest draft: "here is what I promised".
 */
export const COMPOSE_KIND = {
  reply_waiting: "reply_waiting",
  agreement_reply: "agreement_reply",
  first_touch: "first_touch",
  kickoff: "kickoff",
  finance_intro: "finance_intro",
  call_followup: "call_followup",
  nudge_due: "nudge_due",
  final_check_due: "final_check_due",
  pricing_stale: "pricing_stale",
  agreement_stale: "agreement_stale",
  owed: "call_followup",
  wake_up_due: "final_check_due",
  re_engage: "final_check_due",
};

/** What a line becomes once its email has gone: "Replied to Ella Moss". */
export const RECEIPT_WORDS = {
  reply_waiting: (who) => `Replied to ${who}`,
  agreement_reply: (who) => `Replied to ${who}`,
  first_touch: (who) => `First email sent to ${who}`,
  kickoff: (who) => `Welcome sent to ${who}`,
  finance_intro: (who) => `Finance intro sent to ${who}`,
  call_followup: (who) => `Followed up with ${who}`,
  nudge_due: (who) => `Nudged ${who}`,
  final_check_due: (who) => `Checked in with ${who}`,
  pricing_stale: (who) => `Chased ${who}`,
  agreement_stale: (who) => `Chased ${who}`,
  wake_up_due: (who) => `Re-engaged ${who}`,
  re_engage: (who) => `Re-engaged ${who}`,
};

// The × hides these for good (the welcome, the finance intro and the Slack channel
// are one-off handovers), every other line until something newer happens. db/crm.js
// dismissAction owns that rule; the tooltip says it.
const DISMISS_FOR_GOOD = new Set(["kickoff", "finance_intro", "slack_channel"]);

const verbOf = (a) => (a && a.record_verb !== undefined ? a.record_verb : RECORD_VERBS[a && a.kind] ?? null);

/** "just now", "4 min ago", else the clock: how long ago a receipt happened. */
export function sinceWords(at, now = new Date()) {
  const t = toDate(at);
  if (!t) return "";
  const mins = Math.floor((toDate(now).getTime() - t.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return relativeDay(t, { now });
}

/* --------------------------------------------------------------- one line */

function personOf(action, company) {
  const id = action && action.contact_id;
  const contacts = (company && company.contacts) || [];
  const hit = id ? contacts.find((c) => String(c.id) === String(id)) : null;
  return {
    id: id || null,
    name: (action && action.contact_name) || (hit ? fullName(hit) : null),
    first: hit ? firstName(hit) : action && action.contact_name ? String(action.contact_name).split(" ")[0] : null,
    hit: hit || null,
  };
}

/*
 * What the shell says when a line's email has nobody to go to: a welcome or a
 * finance intro on a client with no point of contact (actionContactId is null
 * then). Thrown by ops.compose, so the error lands beside the button that was
 * pressed instead of the yellow doing nothing at all.
 */
export const NO_RECIPIENT = "No one to write to: set a point of contact in People.";

/** The owed deliverable, read off the company's bag (src/actions/model.js readOwed). */
export function owedOf(facts) {
  const raw = facts && facts.owed;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const what = typeof raw.what === "string" ? raw.what.trim() : "";
  return what ? { what, promised_at: raw.promised_at || null, note: raw.note || null } : null;
}

/** "You asked to be reminded in 6 months, on Apr 1": the park's own wording. */
function wakeDetail(company, now) {
  const row = lastTransition(company, "status", "recycled");
  const until = company && company.recycle_until;
  if (!row || !until) return until ? `Wake-up set for ${shortDate(until, { now })}` : null;
  const months = Math.round(daysBetween(row.occurred_at, until) / 30.4);
  const span = months >= 1 ? `in ${plural(months, "month")}` : `on ${shortDate(until, { now })}`;
  return `You asked to be reminded ${span}, on ${shortDate(row.occurred_at, { now })}`;
}

function lineFor(action, ctx) {
  const { company, facts, zones, now } = ctx;
  const kind = action.kind;
  const who = personOf(action, company);
  const verb = verbOf(action);
  const at = action.at || null;
  // THE ADDRESSEE'S CLOCK, not the record's: "Reply waiting · Ella Moss" on
  // Priya's record is about Ella's morning. The record's zones are the person on
  // screen; company.contacts carries everyone's own local_zone (withLocalZones),
  // and one contact zoned by HubSpot's timezone can differ from the rest.
  const theirZones = personZones(who.hit, zones);
  const when = at ? bothZones(at, theirZones, { name: who.first }) : null;
  const day = at ? relativeDay(at, { now }) : null;
  const line = {
    key: `${kind}:${who.id || ""}`,
    kind,
    title: action.label ? action.label.charAt(0).toUpperCase() + action.label.slice(1) : kind,
    detail: action.detail || null,
    contact_id: who.id,
    contact_name: who.name,
    controls: [],
    dismiss: {
      kind,
      anchor: at,
      title: DISMISS_FOR_GOOD.has(kind) ? "Not needed, or done by hand. Hides it for good." : "Not needed, or done by hand",
      forGood: DISMISS_FOR_GOOD.has(kind),
    },
    overdue: !!action.overdue,
    pulse: kind === "reply_waiting" || kind === "agreement_reply",
    hint: HINT_KINDS.has(kind),
    action,
  };
  /*
   * The composer's descriptor carries what the server's draft is written from:
   * age_days is the quiet-days count POST /actions/reply turns into "a few weeks"
   * for the model and logs on the email_drafts row (the old LatestThread and
   * ActionBar passed it; without it every nudge, chase and check-in was drafted
   * as though no time had passed); activity_id and event_id pin a follow-up to
   * the call it is about. `blocked` is said, by the shell, when there is nobody
   * to address.
   */
  const compose = (extra = {}) => ({
    id: "compose",
    label: verb,
    ...(who.id ? {} : { blocked: NO_RECIPIENT }),
    compose: {
      kind: COMPOSE_KIND[kind] || "nudge_due",
      action_kind: kind,
      contact_id: who.id,
      verb,
      age_days: action.age_days ?? null,
      activity_id: action.activity_id || null,
      event_id: action.event_id || null,
      ...extra,
    },
  });

  switch (kind) {
    case "agreement_reply":
      line.title = who.name ? `Reply on the agreement · ${who.name}` : "Reply on the agreement";
      line.detail = [day && day !== "today" ? day : null, when && when.text].filter(Boolean).join(" · ") || action.detail || null;
      line.controls = [compose()];
      break;
    case "reply_waiting":
      line.title = who.name ? `Reply waiting · ${who.name}` : "Reply waiting";
      line.detail = [action.detail, when && when.text].filter(Boolean).join(" · ");
      line.controls = [compose()];
      break;
    case "first_touch": {
      line.title = "New lead · first email";
      const arrived = at || facts.created_at || null;
      const w = arrived ? bothZones(arrived, theirZones, { name: who.first }) : null;
      line.detail = w ? `Arrived ${relativeDay(arrived, { now })} · ${w.text}` : action.detail || null;
      line.controls = [compose()];
      break;
    }
    case "kickoff":
      line.title = "Welcome email";
      line.detail = company && company.won_at ? `Owed since the win, ${shortDate(company.won_at, { now })}` : action.detail;
      line.controls = [compose()];
      break;
    case "finance_intro":
      line.title = "Intro to finance";
      line.detail = company && company.won_at ? `Owed since the win, ${shortDate(company.won_at, { now })}` : action.detail;
      line.controls = [compose()];
      break;
    case "slack_channel":
      // Not an email: the button makes the channel in Slack (POST
      // /companies/:id/slack-channel, src/clientChannel.js) and invites both.
      line.title = action.channel_name ? `Slack channel · #${action.channel_name}` : "Slack channel";
      line.detail = "Internal, with you and Lukasz in it";
      line.controls = [{ id: "slack_channel", label: verb || "Create Slack channel" }];
      break;
    case "call_followup":
      line.title = who.name ? `Follow up from the call · ${who.name}` : "Follow up from the call";
      line.controls = [compose()];
      break;
    case "nudge_due":
      line.title = who.name ? `Nudge due · ${who.name}` : "Nudge due";
      line.controls = [compose()];
      break;
    case "final_check_due":
      line.title = who.name ? `Still relevant? · ${who.name}` : "Still relevant?";
      line.controls = [compose()];
      break;
    case "pricing_stale":
      line.title = "Pricing quiet";
      line.controls = [compose()];
      break;
    case "agreement_stale":
      line.title = "Agreement quiet";
      line.controls = [compose()];
      break;
    case "closing_soon":
      line.title = "No response";
      line.detail = [action.detail, "removing it parks it for 6 months"].filter(Boolean).join(" · ");
      // Not a composer: the last rung is the Remove from pipeline confirm, opened
      // with six months already picked.
      line.controls = [{ id: "remove_from_pipeline", label: verb || "Remove from pipeline", ending: { id: "remove", months: 6 } }];
      break;
    case "owed": {
      const owed = owedOf(facts);
      line.title = owed ? `Owed · ${owed.what}` : "Owed";
      let promised = owed && owed.promised_at ? `Promised ${shortDate(owed.promised_at, { now })}` : null;
      let note = owed && owed.note ? String(owed.note).trim() : null;
      // A note that itself says where it was promised ("Promised in your evening
      // reply to Priya") takes the date instead of following it: "Promised Sep 30.
      // Promised in your…" said the word twice.
      if (promised && note && /^promised\b/i.test(note)) {
        note = `${promised}${note.slice("promised".length)}`;
        promised = null;
      }
      line.detail = [promised, note, "An email doesn’t clear it."].filter(Boolean).join(". ").replace(/\.\./g, ".");
      line.controls = [compose(), { id: "mark_sent", label: "Mark sent" }];
      line.owed = owed;
      break;
    }
    case "call_actions": {
      // What he said on a call he'd send or do (src/calls/promises.js), each ticked
      // off in place (NextList). No button: the work is the items themselves.
      const items = action.items || [];
      const calls = new Set(items.map((x) => x.activity_id)).size;
      line.title = calls > 1 ? "Action items from your calls" : "Action items from the call";
      line.detail = "Tick each one once it’s done. An email doesn’t clear them.";
      line.promises = items;
      line.controls = [];
      break;
    }
    case "wake_up_due":
    case "re_engage": {
      const until = company && company.recycle_until;
      const today = until && daysBetween(now, until) <= 0;
      line.title = kind === "re_engage" ? "Re-engage?" : `Wake-up due · ${today || !until ? "today" : shortDate(until, { now })}`;
      line.detail = wakeDetail(company, now) || action.detail;
      line.controls = [
        compose({ booking_link: true }),
        { id: "move_wakeup", label: "Move the wake-up" },
        { id: "stop_reminders", label: "Stop reminders" },
      ];
      break;
    }
    case "stage_update":
      // No button of its own: it points at the position pill, next to Held /
      // No-show for the meeting it is about.
      line.title = "Update stage?";
      line.detail = facts.last_meeting_at
        ? `Met ${relativeDay(facts.last_meeting_at, { now })}. Change it from the pill above.`
        : "Change it from the pill above.";
      line.pointer = "position_pill";
      line.controls = facts.last_meeting_at
        ? [{ id: "held", label: "Held" }, { id: "no_show", label: "No-show" }]
        : [];
      break;
    case "invite_missing":
      line.title = "Invite sent?";
      line.detail = facts.next_meeting_title
        ? `Nothing on the calendar backs “${facts.next_meeting_title}”.`
        : "The stage says a call is booked, and nothing is on the calendar.";
      line.controls = [];
      break;
    default:
      if (verb) line.controls = [compose()];
  }
  return line;
}

/* ------------------------------------------------------------- the approvals */

const LIFECYCLE_WORDS = {
  lead: "lead",
  marketingqualifiedlead: "MQL",
  salesqualifiedlead: "SQL",
  opportunity: "opportunity",
  customer: "customer",
};
const STATUS_WORDS = {
  client: "client",
  churned: "churned",
  lost: "Closed lost",
  recycled: "Parked",
  not_a_fit: "not a fit",
  vendor: "vendor",
  advisory: "advisory",
  prospect: "back in the pipeline",
};
const STAGE_WORDS = {
  new: "New",
  call_scheduled: "Call scheduled",
  in_progress: "In progress",
  proposal_sent: "Pricing sent",
  evaluating: "Deciding internally",
  agreement: "Agreement + legal",
};
const SOURCE_WORDS = {
  gmail: "an email",
  gmail_pandadoc: "a PandaDoc email",
  calendar: "the calendar",
  fireflies: "a call",
  auditor: "the auditor",
  webflow_form: "the form",
  landing_page: "the landing page",
};

/** "Tom Becker → opportunity, from a PandaDoc email": one waiting change, in words. */
export function pendingWords(p, company) {
  const coName = (company && company.name) || "The company";
  const subject = p.contact_name || coName;
  let to;
  switch (p.change_type) {
    case "contact_stage":
      to = LIFECYCLE_WORDS[p.to] || p.to;
      break;
    case "company_stage":
      to = STAGE_WORDS[p.to] || p.to;
      break;
    case "company_status":
      to = STATUS_WORDS[p.to] || p.to;
      break;
    case "add_to_pipeline":
      to = "on the board";
      break;
    case "create_contact":
      return `New contact ${p.contact_name || (p.payload && p.payload.email) || ""}`.trim();
    case "merge_company": {
      // Without this it printed payload.to, the other company's uuid.
      const q = p.payload || {};
      return q.loser_name
        ? `Merge duplicate ${q.loser_name} (${q.loser_domain || "no domain"}) into ${q.survivor_name || coName}`
        : "Merge a duplicate company";
    }
    case "set_quote": {
      const terms = (p.payload && p.payload.terms) || [];
      const whose = p.payload && p.payload.by === "them" ? "their" : "our";
      return `New pricing: ${terms.join(" · ")}, from ${whose} email`;
    }
    default:
      to = p.to || String(p.change_type || "a change").replace(/_/g, " ");
  }
  const from = SOURCE_WORDS[p.evidence_source] ? `, from ${SOURCE_WORDS[p.evidence_source]}` : "";
  return `${p.change_type === "company_stage" || p.change_type === "company_status" || p.change_type === "add_to_pipeline" ? coName : subject} → ${to}${from}`;
}

function pendingLine(pending, company) {
  const n = pending.length;
  return {
    key: "pending",
    kind: "pending",
    title: `${plural(n, "change")} waiting in Approvals`,
    detail: pending.map((p) => pendingWords(p, company)).join(" · "),
    items: pending.map((p) => ({ id: p.id, text: pendingWords(p, company), evidence: p.evidence || null, proposal: p })),
    contact_id: null,
    contact_name: null,
    // Review opens the waiting changes IN PLACE, with approve, dismiss and correct
    // through the existing /pending-changes routes: nothing navigates away
    // (reality.txt [6]).
    controls: [{ id: "review", label: "Review" }],
    // There is no server-side dismissal for "these are waiting": the × hides the
    // line for this visit, and it is back on the next load while they still wait.
    dismiss: { kind: "pending", local: true, title: "Not now. Back on the next visit while they still wait.", forGood: false },
    overdue: false,
    pulse: false,
    hint: false,
  };
}

/* ------------------------------------------------------------ hold, receipts */

/** "Snoozed until Mon · “waiting on budget sign-off”", or null. */
export function holdLine(hold, { now = new Date() } = {}) {
  if (!hold) return null;
  const note = hold.note ? ` · “${hold.note}”` : "";
  if (hold.kind === "ignored") {
    const why = hold.reason_label ? ` · ${hold.reason_label}` : "";
    const until = hold.forever ? "" : hold.until ? ` until ${dayWords(hold.until, now)}` : "";
    return `Ignored${until}${why}${note}`;
  }
  if (hold.until_reply) return `Snoozed until their next reply${note}`;
  return `Snoozed${hold.until ? ` until ${dayWords(hold.until, now)}` : ""}${note}`;
}

// "Mon" within the week, the date beyond it.
function dayWords(ymd, now) {
  const d = toDate(`${String(ymd).slice(0, 10)}T12:00:00`);
  if (!d) return String(ymd);
  const n = daysBetween(now, d);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n > 1 && n < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return shortDate(d, { now });
}

/**
 * THE RECEIPTS NextList keeps for itself. After a send the action is gone from the
 * reloaded payload, so the line that was there has to be remembered by the card,
 * not read back: `receipts` is [{ kind, contact_id, contact_name, at, thread_url }]
 * in the order they were sent. Owed is the exception and never becomes a receipt:
 * an email does not deliver a promised document, so its line stays, reworded.
 */
function receiptRows(receipts, now) {
  return (receipts || [])
    .filter((r) => r && r.kind !== "owed" && RECEIPT_WORDS[r.kind])
    .map((r) => ({
      key: `receipt:${r.kind}:${r.contact_id || ""}:${r.at}`,
      kind: r.kind,
      text: `${RECEIPT_WORDS[r.kind](r.contact_name || "them")} · ${sinceWords(r.at, now)}`,
      thread_url: r.thread_url || null,
      at: r.at,
    }));
}

/* ------------------------------------------------------------------ the card */

/**
 * The whole card.
 *
 *   lines       [{ key, kind, title, detail, contact_id, contact_name, controls,
 *                  dismiss, overdue, pulse, hint, pointer?, items?, owed?, promises? }]
 *               controls: [{ id, label, compose?, ending? }]; the FIRST control
 *               of a line is its main button, the rest are white beside it.
 *   receipts    [{ key, kind, text, thread_url }], the ✓ rows above the lines
 *   states      plain lines at the foot of the card ("Snoozed until Mon · …")
 *   notes       explanations that belong under a line (Re-engage's booking link)
 *   yellow      { line: 0, control: "compose" } or null: the ONE yellow button
 *   count       lines.length; countLabel "3 to do" (reality.txt [25]: "owed" is
 *               the name of one kind of line, so it is not the count's word)
 *   meeting     the meeting today / tomorrow, for the lead block's Prep line
 *
 * `openAction` is the kind the host's row was showing (Today, Follow-ups,
 * Approvals): that line goes to the top, and the shell opens its composer.
 */
export function nextModel({
  act = null,
  company = null,
  automations = null,
  openAction = null,
  receipts = [],
  dismissedLocal = [],
  zones = null,
  now = new Date(),
} = {}) {
  const facts = (act && act.facts) || {};
  const ctx = { company, facts, zones, now };
  const all = ((act && act.actions) || []).filter((a) => a && a.kind && !NOT_A_LINE.has(a.kind));
  const meetingAction = ((act && act.actions) || []).find((a) => a && NOT_A_LINE.has(a.kind)) || null;

  // A line that has just become a receipt stays one even if the reload has not
  // caught up yet: the same kind for the same person, sent after the action's
  // anchor, is done.
  const sentKeys = new Set(
    (receipts || []).filter((r) => r.kind !== "owed").map((r) => `${r.kind}:${r.contact_id || ""}`)
  );
  let lines = all
    .map((a) => lineFor(a, ctx))
    .filter((l) => !sentKeys.has(l.key));

  // Actionable lines first, hints after: the top line has to be the one with a
  // button, or the card's one yellow would land on "Invite sent?".
  const ranked = [...lines.filter((l) => !l.hint), ...lines.filter((l) => l.hint)];
  lines = ranked;

  if (openAction) {
    const i = lines.findIndex((l) => matchesOpenAction(l.kind, openAction));
    if (i > 0) lines = [lines[i], ...lines.slice(0, i), ...lines.slice(i + 1)];
  }

  const pending = Array.isArray(act && act.pending) ? act.pending : [];
  if (pending.length && !(dismissedLocal || []).includes("pending")) {
    const p = pendingLine(pending, company);
    if (openAction === "pending") lines = [p, ...lines];
    else lines = [...lines, p];
  }

  // The owed exception, after a send: "You emailed Priya just now · case study
  // still owed", with Send and Mark sent kept.
  const lastSent = (receipts || []).filter((r) => r && r.at).slice(-1)[0] || null;
  if (lastSent) {
    lines = lines.map((l) =>
      l.kind === "owed" && l.owed
        ? {
            ...l,
            title: `You emailed ${firstOfName(lastSent.contact_name) || "them"} ${sinceWords(lastSent.at, now)} · ${l.owed.what} still owed`,
            detail: "An email doesn’t clear it",
          }
        : l
    );
  }

  const top = lines[0];
  const yellow = top && top.controls && top.controls[0] && !top.hint && !top.controls[0].proposed
    ? { line: 0, control: top.controls[0].id }
    : null;

  const states = [];
  const hold = act && act.suppression && !act.suppression.derived ? holdLine({
    kind: act.suppression.kind,
    until: act.suppression.until,
    until_reply: act.suppression.until_reply,
    note: act.suppression.note,
    reason_label: act.suppression.reason_label,
    forever: act.suppression.forever,
  }, { now }) : null;
  if (hold && (act.suppression.kind === "snoozed" || act.suppression.kind === "ignored")) states.push(hold);
  const quiet = autoReplyQuiet(lines, automations, company);
  if (quiet) states.push(quiet);

  const notes = [];
  if (lines.some((l) => l.kind === "wake_up_due" || l.kind === "re_engage")) {
    const name = (company && company.name) || "They";
    notes.push(`Re-engage drafts an email with your booking link. ${name} comes back on the board by itself when they write.`);
  }

  return {
    lines,
    receipts: receiptRows(receipts, now),
    states,
    notes,
    yellow,
    count: lines.length,
    countLabel: lines.length ? `${lines.length} to do` : "",
    meeting: meetingAction
      ? {
          kind: meetingAction.kind,
          at: facts.next_meeting_at || meetingAction.at || null,
          title: facts.next_meeting_title || null,
          contact_id: meetingAction.contact_id || null,
        }
      : null,
  };
}

/*
 * WHY NO AUTO-REPLY RAN, on a new lead still owed the first email (frame 1c: "No
 * auto-reply ran: she wrote inside your hours."): the one state on a new lead that
 * changes what I do, because it says the lead has heard nothing from us yet. Said
 * only when it is true for that reason: the after-hours reply is switched on (or in
 * dry run, which would still have logged a row), it has no auto_reply_logs row here
 * at all, and GET /companies/:id/automations says the form arrived outside an armed
 * window (armed_at_arrival, isArmed of the form's time against the automation's own
 * schedule). Any logged run, another reason, or an older server that does not send
 * the flag: nothing is said, because a guessed reason is worse than none. The
 * frame's "she" is a guess about the person too; the record says their first name.
 */
export function autoReplyQuiet(lines, automations, company) {
  const first = (lines || []).find((l) => l && l.kind === "first_touch");
  if (!first) return null;
  const list = (automations && automations.automations) || [];
  const auto = list.find((a) => a && a.type === "after_hours_auto_reply");
  if (!auto || auto.off || !auto.enabled || auto.last_run) return null;
  if (auto.armed_at_arrival !== false) return null;
  const hit = ((company && company.contacts) || []).find((c) => first.contact_id && String(c.id) === String(first.contact_id));
  const who = (hit && firstName(hit)) || firstOfName(first.contact_name) || "they";
  return `No auto-reply ran: ${who} wrote inside your hours.`;
}

/*
 * Is this line the one the host's row was showing? Exact, except that a queue that
 * only knows "a reply is owed" (Follow-ups' pinned section) says reply_waiting, and
 * on a company with a contract out the same debt is the agreement_reply kind.
 */
export function matchesOpenAction(kind, openAction) {
  if (!openAction) return false;
  if (kind === openAction) return true;
  return openAction === "reply_waiting" && kind === "agreement_reply";
}

function firstOfName(name) {
  return name ? String(name).trim().split(/\s+/)[0] : null;
}

/** Every control except the yellow one: the phone's More sheet. */
export function moreControls(model) {
  const out = [];
  (model && model.lines ? model.lines : []).forEach((l, i) => {
    (l.controls || []).forEach((c, j) => {
      if (model.yellow && model.yellow.line === i && j === 0) return;
      out.push({ line: l.key, title: l.title, ...c });
    });
  });
  return out;
}
